'use client';
import { useScrollToItemOnHash } from '@vs-shop/headless/useScrollToItemOnHash';
import { mapProductsToCards, resolveClientProductCardData } from '../../../product/index';
import { toProductImagePreview } from '../../../product/productImagePreview';
import type { GtmListIdTypeT } from '@vs-shop/schemas/gtm/list-types';
import ProductCardSkeleton from '../../server/product/card/ProductCardSkeleton';
import { updateUrlOnLinkClick } from '../../../shared/utils/updateUrlOnLinkClick';
import { Chunk, Option } from 'effect';
import { useIntl } from 'react-intl';

import { productRecommendedProductsPerPageDefault } from '../../../config/pagination';
import { accessoriesPageParamName } from '../../../config/urlParams';

import ProductCardRecommendationAddToCart from '../product/ProductCardRecommendationAddToCart';
import ProductCard from '../../server/product/card/ProductCard';
import ProductCardHeader from '../../server/product/card/ProductCardHeader';
import ProductCardImage from '../../server/product/card/ProductCardImage';
import ProductCardMediaLink from '../../server/product/card/ProductCardMediaLink';
import ProductCardPriceStock from '../../server/product/card/ProductCardPriceStock';
import ProductCardStickers from '../../server/product/card/ProductCardStickers';
import ProductCardTracker from '../../server/product/card/ProductCardTracker';
import ProductCardCoupon from '../../server/product/ProductCardCoupon';

import { fetchAssociatedProductsPageAtom } from '../../../effect/atom/products/fetchAssociatedProductsPage';
import {
  accessoriesLoadMorePagesAtom,
  accessoriesTaxonSearchParamAtom,
  currentAccessoriesPageAtom,
} from '../../../effect/atom/products/recommendationsPagination';
import { ProductCardVariant } from '@vs-shop/commerce-ui/productCard';
import {
  ProductsGrid,
  ProductsGridItem,
  ProductsGridVariant,
} from '@vs-shop/commerce-ui/productsGrid';
import { EmptyState, EmptyStateVariant } from '@vs-shop/commerce-ui/emptyState';
import * as AsyncResult from 'effect/unstable/reactivity/AsyncResult';
import { useAtomValue } from '@effect/atom-react';

/** Only the field `handleProductNavigation` reads. */
type HasProductSlug = {
  readonly slug: string;
};

type Props = {
  productCode: string;
  listType: GtmListIdTypeT;
  itemsPerPage: number;
  initialTaxon: string | null;
};

export default function RecommendationsGrid({
  productCode,
  listType,
  itemsPerPage,
  initialTaxon,
}: Props) {
  const intl = useIntl();

  useScrollToItemOnHash();

  const currentTaxonOption = useAtomValue(accessoriesTaxonSearchParamAtom);

  const currentPage = useAtomValue(currentAccessoriesPageAtom);
  const loadMorePages = useAtomValue(accessoriesLoadMorePagesAtom);

  const taxonCode = Option.match(currentTaxonOption, {
    onNone: () => initialTaxon,
    onSome: (code) => code,
  });

  const startPage = currentPage - loadMorePages;
  const endPage = currentPage;

  const productsPerPageResponse = useAtomValue(
    fetchAssociatedProductsPageAtom(productCode, taxonCode, startPage, itemsPerPage)
  );

  return AsyncResult.builder(productsPerPageResponse)
    .onInitial(() => {
      return (
        <div>
          <ProductsGrid variant={ProductsGridVariant.RECOMMENDATION}>
            {Array.from({ length: productRecommendedProductsPerPageDefault }, (_, index) => (
              <ProductsGridItem key={`skeleton-${String(index)}`}>
                <ProductCardSkeleton variant={ProductCardVariant.RECOMMENDATION} />
              </ProductsGridItem>
            ))}
          </ProductsGrid>
        </div>
      );
    })
    .onSuccess((success) => {
      const totalMembers = success['hydra:totalItems'];

      if (totalMembers === 0) {
        return (
          <EmptyState variant={EmptyStateVariant.INLINE} className="p-4 text-xl">
            {intl.formatMessage({
              id: 'component.client.products.recommendationsGrid.noProducts',
              defaultMessage: 'Nenalezeny žádné produkty',
            })}
          </EmptyState>
        );
      }

      return (
        <div>
          <ProductsGrid variant={ProductsGridVariant.RECOMMENDATION}>
            {Chunk.range(startPage, endPage).pipe(
              Chunk.map((page) => (
                <RecommendationsGridPerPage
                  key={page}
                  productCode={productCode}
                  page={page}
                  listType={listType}
                  itemsPerPage={itemsPerPage}
                />
              )),
              Chunk.toReadonlyArray
            )}
          </ProductsGrid>
        </div>
      );
    })
    .render();
}

type RecommendationsGridPerPageProps = {
  productCode: string;
  page: number;
  listType: GtmListIdTypeT;
  itemsPerPage: number;
};

function RecommendationsGridPerPage({
  productCode,
  page,
  listType,
  itemsPerPage,
}: RecommendationsGridPerPageProps) {
  const currentTaxonOption = useAtomValue(accessoriesTaxonSearchParamAtom);
  const currentLoadMoreAccessories = useAtomValue(accessoriesLoadMorePagesAtom);

  const taxonCode = Option.match(currentTaxonOption, {
    onNone: () => null,
    onSome: (code) => code,
  });

  const productsPerPageResponse = useAtomValue(
    fetchAssociatedProductsPageAtom(productCode, taxonCode, page, itemsPerPage)
  );

  const productMembers = AsyncResult.match(productsPerPageResponse, {
    onInitial: () => [],
    onFailure: () => [],
    onSuccess: ({ value }) => value['hydra:member'],
  });

  const products = mapProductsToCards(productMembers);

  if (AsyncResult.isInitial(productsPerPageResponse)) {
    return Array.from({ length: productRecommendedProductsPerPageDefault }, (_, index) => (
      <ProductsGridItem key={`page-${String(page)}-skeleton-${String(index)}`}>
        <ProductCardSkeleton variant={ProductCardVariant.RECOMMENDATION} />
      </ProductsGridItem>
    ));
  }

  const handleProductNavigation = (product: HasProductSlug) => {
    //is related only to load more navigation
    if (currentLoadMoreAccessories === 0) return;
    updateUrlOnLinkClick(page, accessoriesPageParamName, product.slug);
  };

  return products.map((product) => (
    <ProductsGridItem
      key={product.code}
      data-hash-identifier={product.slug}
      onClick={() => {
        handleProductNavigation(product);
      }}
    >
      <ProductCardTracker listType={listType} product={resolveClientProductCardData(product)}>
        <ProductCard
          variant={ProductCardVariant.RECOMMENDATION}
          header={
            <ProductCardHeader
              productName={product.name}
              routerUrl={product.routerUrl}
              metaTitle={product.metaTitle}
            />
          }
          media={
            <ProductCardMediaLink
              routerUrl={product.routerUrl}
              metaTitle={product.metaTitle}
              variant={ProductCardVariant.RECOMMENDATION}
            >
              <ProductCardImage
                imagePath={product.imagePath}
                alt={product.name}
                variant={ProductCardVariant.RECOMMENDATION}
              />
              <ProductCardStickers
                labels={product.labels}
                ageRestriction={product.ageRestriction}
                price={product.price}
                originalPrice={product.originalPrice}
              />
            </ProductCardMediaLink>
          }
          promo={
            <ProductCardCoupon
              productCode={product.code}
              productName={product.name}
              discountBanner={product.discountBanner}
              price={product.price}
              priceWithoutTax={product.priceWithoutTax}
              stock={product.stock}
            />
          }
          description={product.subtitle ?? product.description}
          footer={
            <>
              <ProductCardPriceStock
                price={product.price}
                originalPrice={product.originalPrice}
                stock={product.stock}
                labels={product.labels}
                variant={ProductCardVariant.RECOMMENDATION}
              />
              <ProductCardRecommendationAddToCart
                productCode={product.code}
                productName={product.name}
                imagePreview={toProductImagePreview(product.imagePath)}
                ageRestriction={product.ageRestriction}
                price={Option.getOrElse(product.price, () => 0)}
                priceWithoutTax={Option.getOrElse(product.priceWithoutTax, () => 0)}
                stock={product.stock}
                className="w-full px-2 text-sm tracking-normal @[320px]:text-base"
              />
            </>
          }
        />
      </ProductCardTracker>
    </ProductsGridItem>
  ));
}
